/* eslint-disable arrow-body-style */
import { useState, useCallback, useMemo, useEffect } from 'react';
import moment, { isMoment } from 'moment';
import { useForm, useField } from 'react-final-form-hooks';
import useConstants from '../_config/constant';
import { isMultipleEmails } from '@/pages/dashboard/utils/utils';

interface Props {
  formValue: {
    [key: string]: any;
  };
  validate?: any;
  formatMessage?: any;
  receiverList?: any[];
  isUseOutsideEmail: boolean;
}

const MATCH_SCRIPT_TAG = /(<script>|<\/script>|<script\/>)/;

const useFormField = (props: Props) => {
  const { formValue: formValueProps, validate, receiverList, formatMessage, isUseOutsideEmail } = props;
  const timeVal = formValueProps?.cycle;
  if (timeVal) {
    // 其实可以在跳转的时候改url中的参数，但是moment格式太长了。传入url不太合适，所以在此做处理
    const cycleStart = isMoment(timeVal?.[0]) ? timeVal?.[0] : moment(timeVal?.[0]);
    const cycleEnd = isMoment(timeVal?.[1]) ? timeVal?.[1] : moment(timeVal?.[1]);
    formValueProps.cycle = [cycleStart, cycleEnd];
  }
  // #region 状态定义
  const { SETTING_FIELDS, INITIAL_VALUES, FIELD_STATUS } = useConstants();
  // 表单状态
  const { SUCCESS, ERROR, VALIDATING } = FIELD_STATUS;
  // 表单字段
  const {
    RECEIVERS,
    TITLE,
    CONTENT,
    WAYS,
    FREQUENCY,
    CYCLE,
    SENDOUTEMAIL,
    VIEWPORT_WIDTH
  } = SETTING_FIELDS;

  const [formValue, setFormValue] = useState<any>(formValueProps ?? INITIAL_VALUES);

  const isSelectOutEmail = useMemo(() => {
    return isUseOutsideEmail && formValue?.[SETTING_FIELDS.SENDOUTEMAIL] === 'send';
  }, [isUseOutsideEmail, formValue?.[SETTING_FIELDS.SENDOUTEMAIL]]);

  useEffect(() => {
    setFormValue(formValueProps);
  }, [formValueProps?.receivers?.toString()]);


  const { form, handleSubmit, validating } = useForm({
    onSubmit: () => { },
    initialValues: formValue,
    validate: validate ?? ((fields: any) => {
      let recivers = fields?.[SETTING_FIELDS.RECEIVERS];
      // const reciversIsString = typeof(recivers)=== 'string'
      let receiverOutEmailMsg = undefined;
      if (isSelectOutEmail) {
        receiverOutEmailMsg = !isMultipleEmails(recivers) ? formatMessage({ id: 'subscribe_setting_input_label_out_email_placeholder' }) : undefined;
      } else {
        // 如果是外部邮箱的记录，且关闭了外部邮箱的开关，那么收件人回显到页面就是直接展示为空
        if (typeof (recivers) === 'string') {
          recivers = [];
          setFormValue({
            ...(formValue ?? {}),
            [SETTING_FIELDS.RECEIVERS]: []
          });
        }
      }

      return ({
        [SETTING_FIELDS.RECEIVERS]: isSelectOutEmail ? receiverOutEmailMsg : recivers?.filter((item: string) => item !== '')?.length < 1 ? formatMessage({ id: 'subscribe_setting_input_empty_receiver' }) : undefined,
        [SETTING_FIELDS.TITLE]: fields?.[SETTING_FIELDS.TITLE]?.length < 1 ? formatMessage({ id: 'subscribe_setting_input_empty_title' }) : undefined,
        [SETTING_FIELDS.CONTENT]: fields?.[SETTING_FIELDS.CONTENT]?.length > 1000 ? formatMessage({ id: 'subscribe_setting_input_error_content' }) : undefined,
        [SETTING_FIELDS.WAYS]: fields?.[SETTING_FIELDS.WAYS]?.length < 1 ? formatMessage({ id: 'subscribe_setting_input_empty_ways' }) : undefined,
        [SETTING_FIELDS.FREQUENCY]: fields?.[SETTING_FIELDS.FREQUENCY]?.frequency_interval?.length < 1 ? formatMessage({ id: 'subscribe_setting_input_empty_frequency' }) : undefined,
        [SETTING_FIELDS.CYCLE]: fields?.[SETTING_FIELDS.CYCLE]?.length < 1 ? '订阅周期不能为空' : undefined,
      });
    })
  });
  const {
    hasValidationErrors
  } = form?.getState() ?? {};
  const receivers = useField(RECEIVERS, form);

  const title = useField(TITLE, form);
  const content = useField(CONTENT, form);
  const ways = useField(WAYS, form);
  const frequency = useField(FREQUENCY, form);
  const cycle = useField(CYCLE, form);
  const sendOutEmail = useField(SENDOUTEMAIL, form);
  const viewportWidth = useField(VIEWPORT_WIDTH, form);

  // 获取表单状态
  const getStatus: (meta: any, validating: any) => 'success' | 'error' | 'validating' = useCallback(
    (meta, validating) => {
      const errorMessage = meta?.error;

      if (meta.active && validating) {
        return VALIDATING;
      }
      if (!meta.touched) {
        return SUCCESS;
      }
      return errorMessage?.length > 0 ? ERROR : SUCCESS;
    }, []);

  // 获取错误文本
  const getErrorMessage = (meta: { error: any; }, validating: any) => {
    const status = getStatus(meta, validating);
    return status === ERROR ? meta?.error : null;
  };

  const recivers = formValue?.[SETTING_FIELDS.RECEIVERS];
  const reciversIsString = typeof (recivers) === 'string';
  return {
    formValue,
    setFormValue,
    input: {
      [RECEIVERS]: {
        ...receivers.input,
        value: reciversIsString ? recivers : recivers?.filter((item: string) => item !== ''),
        onChange: (value: any[] | string) => {
          let result;
          if (isSelectOutEmail) {
            // 去空格？
            result = value?.replace(MATCH_SCRIPT_TAG, 'script');
          } else {
            result = value?.filter((item: object) => receiverList?.some(receiver => receiver?.value === item));
          }
          setFormValue({
            ...(formValue ?? {}),
            [SETTING_FIELDS.RECEIVERS]: result
          });
        }
      },
      [TITLE]: {
        ...title.input,
        value: formValue?.[SETTING_FIELDS.TITLE],
        onChange: (value: any) => (
          setFormValue({
            ...(formValue ?? {}),
            [SETTING_FIELDS.TITLE]: value?.replace(MATCH_SCRIPT_TAG, 'script')
          })
        )
      },
      [CONTENT]: {
        ...content.input,
        value: formValue?.[SETTING_FIELDS.CONTENT],
        onChange: (value: string) => (
          setFormValue({
            ...(formValue ?? {}),
            [SETTING_FIELDS.CONTENT]: value?.replace(MATCH_SCRIPT_TAG, 'script')
          })
        )
      },
      [WAYS]: {
        ...ways.input,
        value: formValue?.[SETTING_FIELDS.WAYS],
        onChange: (value: any) => {
          setFormValue({
            ...(formValue ?? {}),
            [SETTING_FIELDS.WAYS]: value
          });
        }
      },
      [SENDOUTEMAIL]: {
        ...sendOutEmail.input,
        value: formValue?.[SETTING_FIELDS.SENDOUTEMAIL],
        onChange: (value: any) => {
          const isSend = value === 'send';
          setFormValue({
            ...(formValue ?? {}),
            [SETTING_FIELDS.SENDOUTEMAIL]: value,
            [SETTING_FIELDS.RECEIVERS]: [],
            [SETTING_FIELDS.WAYS]: isSend ? ['1'] : formValue?.[SETTING_FIELDS.WAYS]
          });
        }
      },
      [FREQUENCY]: {
        ...frequency.input,
        value: formValue?.[SETTING_FIELDS.FREQUENCY],
        onChange: (value: any) => {
          setFormValue({
            ...(formValue ?? {}),
            [SETTING_FIELDS.FREQUENCY]: value
          });

        }
      },
      [CYCLE]: {
        ...cycle.input,
        value: formValue?.[SETTING_FIELDS.CYCLE],
        onChange: (value: any) => {
          setFormValue({
            ...(formValue ?? {}),
            [SETTING_FIELDS.CYCLE]: value
          });
        }
      },
      [VIEWPORT_WIDTH]: {
        ...viewportWidth.input,
        value: formValue?.[SETTING_FIELDS.VIEWPORT_WIDTH],
        onChange: (value: any) => {
          setFormValue({
            ...(formValue ?? {}),
            [SETTING_FIELDS.VIEWPORT_WIDTH]: value
          });
        }
      },
    },
    meta: {
      [RECEIVERS]: {
        ...receivers.meta,
        errorMessage: getErrorMessage(receivers.meta, validating),
        status: getStatus(receivers.meta, validating)
      },
      [TITLE]: {
        ...title.meta,
        errorMessage: getErrorMessage(title.meta, validating),
        status: getStatus(title.meta, validating)
      },
      [CONTENT]: {
        ...content.meta,
        errorMessage: getErrorMessage(content.meta, validating),
        status: getStatus(content.meta, validating)
      },
      [WAYS]: {
        ...ways.meta,
        errorMessage: getErrorMessage(ways.meta, validating),
        status: getStatus(ways.meta, validating)
      },
      [FREQUENCY]: {
        ...frequency.meta,
        errorMessage: getErrorMessage(frequency.meta, validating),
        status: getStatus(frequency.meta, validating)
      },
      [CYCLE]: {
        ...cycle.meta,
        errorMessage: getErrorMessage(cycle.meta, validating),
        status: getStatus(cycle.meta, validating)
      },
    },
    hasErrors: hasValidationErrors,
    getStatus,
    handleSubmit,
    restForm: () => {
      setFormValue(formValueProps ?? INITIAL_VALUES);
    }
  };
};

export default useFormField;
